import { t } from '@app/i18n';
import { useNavigate } from 'react-router-dom';
import { identityAccessHref } from '@app/navigation';
import { Badge, Button, Card, Chip, IconButton } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import { agentState } from '@app/main/Resources/resourceVocab';
import type { AgentState } from '@app/main/Resources/Resources.api';
import { useMultiSite } from '@app/main/Sites/Sites.api';
import type { MapEdge, MapNode } from './AccessMap.api';
import {
  typeLabel,
  EMPTY_DETAIL,
  inSubtitle,
  limitsText,
  inKind,
  detailValue,
  reachText,
} from './accessMapVocab';
import s from './AccessMap.module.css';

/**
 * Painel **Detalhe** de T19 (FR-103) — o conteúdo muda por tipo de nó, e as
 * ações levam para onde se muda o que o mapa mostra: a identidade, o Access
 * Control e a política.
 *
 * "Pode usar" vem agrupado por origem — pelo contrato e por cada política — que
 * é a pergunta que o mapa existe para responder: **de onde vem** esta permissão.
 */

export interface DetailPanelProps {
  no: MapNode | null;
  /** Todas as arestas do mapa, sem colapso: a origem tem de ficar visível. */
  arestas: MapEdge[];
  nos: MapNode[];
  onFechar: () => void;
  /** Repassada ao `Card`: dentro do card grande de T19 ele perde a moldura. */
  className?: string;
  /**
   * O painel está dentro da aba «Detalhe» do card de T19. Só muda o vazio: a
   * aba já diz «Detalhe», e repetir o título logo abaixo dela é ruído. Com um
   * nó selecionado o título é o nome do nó, que não se repete em lugar nenhum.
   */
  embutido?: boolean;
}

function labelOf(nos: MapNode[], id: string): string {
  return nos.find((no) => no.id === id)?.label ?? id;
}

/** `faturas.consultar · max_amount ≤ 5000`. */
function itemDaTool(edge: MapEdge): string {
  const limits = limitsText(edge);
  return limits ? `${edge.label} · ${limits}` : (edge.label ?? 'tool');
}

/**
 * A tool com o veredito da lente ao lado — o diagnóstico **por eixo**, no mesmo
 * vocabulário de T18.
 *
 * Fora da lente é só o chip de sempre: `alcance` nulo significa "ninguém
 * perguntou", que é diferente de "perguntei e não passa".
 */
function ToolItem({ edge }: { edge: MapEdge }) {
  const reach = edge.reach;
  if (!reach) return <Chip>{itemDaTool(edge)}</Chip>;

  // A frase no idioma da tela, pelos campos do alcance — e não o `diagnostic` da API, que é inglês (aim-6ph.23).
  const axes = reachText(edge);
  return (
    <span
      className={s.toolWithAxes}
      data-testid="tool-with-axes"
      data-reach={reach.reached ? 'sim' : 'nao'}
    >
      <Chip selected={reach.reached}>{itemDaTool(edge)}</Chip>
      {axes ? <span className={s.axes}>{axes}</span> : null}
    </span>
  );
}

export function DetailPanel({ no, arestas, nos, onFechar, className, embutido }: DetailPanelProps) {
  const navigate = useNavigate();

  if (!no) {
    return (
      <div data-testid="painel-detalhe">
        <Card
          className={className}
          title={embutido ? undefined : t('accessMap.detail.title')}
          aria-label={t('accessMap.detail.title')}
        >
          {/* Vazio do protótipo (`.empty`): ícone grande + a frase, centrados. */}
          <div className={s.painelVazio}>
            <Icon name="exchange" size="xl" aria-hidden="true" />
            <p className={s.vazioTexto}>{EMPTY_DETAIL()}</p>
          </div>
        </Card>
      </div>
    );
  }

  const kind = inKind(no);

  return (
    <div data-testid="painel-detalhe" data-tipo={no.kind} data-rotulo={no.label}>
      <Card
        className={className}
        title={
          <span className={s.tituloDetalhe}>
            {no.label}
            <Badge tone="neutral">{typeLabel(kind)}</Badge>
          </span>
        }
        subtitle={inSubtitle(no)}
        toolbar={
          <IconButton
            icon="close"
            label={t('accessMap.detail.clearSelection')}
            size="sm"
            onClick={onFechar}
          />
        }
      >
        <SiteRow no={no} />
        {no.kind === 'identidade' ? (
          <IdentityDetail no={no} arestas={arestas} nos={nos} navegar={navigate} />
        ) : null}
        {no.kind === 'resource' && no.subtype === 'agent' ? <ExternalAgentDetail no={no} /> : null}
        {no.kind === 'resource' && no.subtype !== 'agent' ? (
          <ResourceDetail no={no} arestas={arestas} nos={nos} />
        ) : null}
        {no.kind === 'human_subject' ? <HumanDetail no={no} arestas={arestas} nos={nos} /> : null}
      </Card>
    </div>
  );
}

/**
 * Onde este nó roda — faixa B de §5.6, forma (1).
 *
 * **Um componente só para os dois tipos**, e não uma linha repetida dentro de
 * `IdentityDetail` e de `ResourceDetail`: duas cópias do mesmo rótulo divergem
 * na primeira vez que alguém mexer numa delas. O nó humano da lente não tem
 * site — não é workload nenhum —, e por isso o filtro é por `kind` aqui e não
 * lá dentro.
 *
 * Cala-se em três casos, e os três são «não há o que dizer»: com um site só
 * (§3.1, a dica diria sempre a mesma coisa), no nó humano, e quando o slug vem
 * vazio — o resource resolve o slug por join no cadastro de sites, e vazio ali
 * significa «o cadastro não respondeu», nunca «não tem site».
 */
function SiteRow({ no }: { no: MapNode }) {
  const multiSite = useMultiSite();
  const slug = detailValue<string>(no, 'site_slug');
  if (!multiSite || !slug) return null;
  if (no.kind !== 'identidade' && no.kind !== 'resource') return null;
  // O SLUG cru (§3.2) — é ele que entra no SPIFFE ID e o que se vê no
  // `kubectl`. Nunca o `name`, que é livre e repete; nunca o uuid.
  return <Row rotulo={t('accessMap.detail.site')}>{slug}</Row>;
}

type Navegar = ReturnType<typeof useNavigate>;

function Row({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className={s.linhaDetalhe}>
      <span className={s.rotuloDetalhe}>{rotulo}</span>
      <span className={s.valorDetalhe}>{children}</span>
    </div>
  );
}

function IdentityDetail({
  no,
  arestas,
  nos,
  navegar,
}: {
  no: MapNode;
  arestas: MapEdge[];
  nos: MapNode[];
  navegar: Navegar;
}) {
  const spiffe = detailValue<string>(no, 'spiffe_id');
  const contrato = detailValue<number | null>(no, 'contrato_version');
  const whoCanInvoke = detailValue<string[]>(no, 'quem_pode_invocar') ?? [];

  const saindo = arestas.filter((edge) => edge.from === no.id);
  // A2A é do CONTRATO e só (03/09) — não há mais «invocação por política».
  const invocations = saindo.filter((edge) => edge.kind === 'invoke_agent');

  const contractTools = saindo.filter((edge) => edge.kind === 'tool' && edge.origin === 'contrato');
  /**
   * As tools que uma regra de Human Access CITA e o contrato não expõe. Só
   * existem sob a lente, e vêm com `origem: null` — nem o contrato nem a regra
   * as concedem.
   *
   * Ficam num grupo à parte, e nunca em «Pelo contrato»: listá-las como acesso
   * seria repetir na tabela a união que o mapa acabou de tirar do desenho.
   */
  const citedTools = saindo.filter((edge) => edge.kind === 'tool' && edge.origin == null);

  return (
    <>
      <Row rotulo={t('accessMap.detail.spiffeId')}>
        <span className={s.mono}>{spiffe ?? t('accessMap.detail.unassigned')}</span>
      </Row>
      <Row rotulo={t('accessMap.detail.contract')}>
        {contrato == null ? (
          <span className={s.vazio}>{t('accessMap.node.noContract')}</span>
        ) : (
          t('accessMap.detail.contractVersionShort', { version: contrato })
        )}
      </Row>

      <h4 className={s.secao}>{t('accessMap.detail.canInvoke')}</h4>
      {invocations.length === 0 ? (
        <p className={s.vazioTexto}>{t('accessMap.detail.noIdentity')}</p>
      ) : (
        <ul className={s.itens}>
          {invocations.map((edge) => (
            <li key={`c-${edge.to}`}>
              {labelOf(nos, edge.to)}{' '}
              <span className={s.origem}>{t('accessMap.detail.viaContract')}</span>
            </li>
          ))}
        </ul>
      )}

      <h4 className={s.secao}>{t('accessMap.detail.canUse')}</h4>
      {contractTools.length === 0 ? (
        <p className={s.vazioTexto}>{t('accessMap.detail.noTool')}</p>
      ) : null}

      {contractTools.length > 0 ? (
        <>
          <p className={s.grupo}>
            {t('accessMap.detail.byContractVersion', { version: contrato ?? '—' })}
          </p>
          <span className={s.chips}>
            {contractTools.map((edge) => (
              <ToolItem key={`t-${edge.to}-${edge.label}`} edge={edge} />
            ))}
          </span>
        </>
      ) : null}

      {citedTools.length > 0 ? (
        <>
          <p className={s.grupo}>{t('accessMap.detail.assertedByRule')}</p>
          <p className={s.origem}>{t('accessMap.detail.assertedByRuleHelp')}</p>
          <span className={s.chips}>
            {citedTools.map((edge) => (
              <ToolItem key={`ct-${edge.to}-${edge.label}`} edge={edge} />
            ))}
          </span>
        </>
      ) : null}

      <h4 className={s.secao}>{t('accessMap.detail.whoCanInvoke')}</h4>
      {whoCanInvoke.length === 0 ? (
        <p className={s.vazioTexto}>{t('accessMap.detail.nobody')}</p>
      ) : (
        <span className={s.chips}>
          {whoCanInvoke.map((nome) => (
            <Chip key={nome}>{nome}</Chip>
          ))}
        </span>
      )}

      <span className={s.acoesDetalhe}>
        <Button variant="out" onClick={() => navegar(`/identidades?drawer=${no.ref_id ?? ''}`)}>
          {t('accessMap.detail.openIdentity')}
        </Button>
        <Button variant="out" onClick={() => navegar(identityAccessHref(no.ref_id ?? ''))}>
          {t('accessMap.detail.openIdentityAccess')}
        </Button>
      </span>
    </>
  );
}

/**
 * O agente externo (fase 2, decisão 24): o caminho no gateway, o estado e quem pode invocá-lo pelo contrato. O
 * detalhe completo — card, operação, credencial — está em Recursos (T7), e o botão leva até lá.
 */
function ExternalAgentDetail({ no }: { no: MapNode }) {
  const navigate = useNavigate();
  const path = detailValue<string>(no, 'agent_path') ?? '';
  const state = detailValue<AgentState>(no, 'agent_state');
  const callers = detailValue<string[]>(no, 'quem_pode_invocar') ?? [];
  const href = detailValue<{ editar?: string }>(no, 'links')?.editar;
  return (
    <>
      <Row rotulo={t('accessMap.detail.agentPath')}>
        <span className={s.mono}>{`/a2a/${path}/`}</span>
      </Row>
      {state ? (
        <Row rotulo={t('accessMap.detail.agentState')}>
          <Badge tone={agentState(state).tone}>{agentState(state).rotulo}</Badge>
        </Row>
      ) : null}
      <h4 className={s.secao}>{t('accessMap.detail.whoCanInvoke')}</h4>
      {callers.length === 0 ? (
        <p className={s.vazioTexto}>{t('accessMap.detail.nobodyInvokes')}</p>
      ) : (
        <span className={s.chips}>
          {callers.map((slug) => (
            <Chip key={slug}>{slug}</Chip>
          ))}
        </span>
      )}
      {href ? (
        <Button variant="out" onClick={() => navigate(href)}>
          {t('accessMap.detail.openAgent')}
        </Button>
      ) : null}
    </>
  );
}

function ResourceDetail({ no, arestas, nos }: { no: MapNode; arestas: MapEdge[]; nos: MapNode[] }) {
  const endpoint = detailValue<string | null>(no, 'endpoint');
  const owner = detailValue<string | null>(no, 'owner');
  const withoutAccess = detailValue<string[]>(no, 'tools_sem_acesso') ?? [];

  /**
   * Quem acessa: SEMPRE uma identidade, e sempre pelo contrato (04/09). Não há
   * mais nó de política de onde uma aresta pudesse chegar aqui.
   *
   * As citadas da lente (`origem: null`) ficam de fora: elas apontam para este
   * resource e ninguém as alcança — listá-las em «Quem acessa» diria o contrário
   * do que a própria aresta esmaecida mostra. Elas continuam contadas em
   * `tools_sem_acesso`, que é onde se nota a regra que nunca funcionou.
   */
  const inputs = arestas.filter(
    (edge) => edge.to === no.id && edge.kind === 'tool' && edge.origin === 'contrato',
  );
  const accesses = inputs.map((edge) => ({
    quem: labelOf(nos, edge.from),
    aresta: edge,
    via: t('accessMap.detail.viaContract'),
  }));

  return (
    <>
      <Row rotulo={t('accessMap.detail.endpoint')}>
        {endpoint ? (
          <span className={s.mono}>{endpoint}</span>
        ) : (
          <span className={s.vazio}>{t('accessMap.detail.notConfigured')}</span>
        )}
      </Row>
      <Row rotulo={t('accessMap.detail.owner')}>
        {owner ?? <Badge tone="warning">{t('accessMap.detail.noOwner')}</Badge>}
      </Row>

      <h4 className={s.secao}>{t('accessMap.detail.whoAccessesWithWhat')}</h4>
      {accesses.length === 0 ? (
        <p className={s.vazioTexto}>{t('accessMap.detail.noIdentityAccessesResource')}</p>
      ) : (
        <ul className={s.itens}>
          {accesses.map((access, index) => (
            <li key={`${access.quem}-${index}`}>
              <strong>{access.quem}</strong> <Chip>{itemDaTool(access.aresta)}</Chip>{' '}
              <span className={s.origem}>{access.via}</span>
            </li>
          ))}
        </ul>
      )}

      <h4 className={s.secao}>{t('accessMap.detail.toolsWithoutAccess')}</h4>
      {withoutAccess.length === 0 ? (
        <p className={s.vazioTexto}>{t('accessMap.detail.allToolsHaveAccess')}</p>
      ) : (
        <span className={s.chips}>
          {withoutAccess.map((tool) => (
            <Chip key={tool}>{tool}</Chip>
          ))}
        </span>
      )}
    </>
  );
}

/**
 * Detalhe do nó humano — que, desde a Fase 3, só existe sob a lente «Ver como».
 *
 * O texto explica o que a lente é e o que ela NÃO é: uma leitura hipotética
 * («se alguém com estas claims pedisse»), não a chamada de uma pessoa real. Sem
 * isso o operador leria o realce como registro de acesso. A frase é do catálogo
 * da tela, em pt-BR e en, e não do servidor (aim-6ph.22, aprovado pelo mrolim em
 * 10/10/2026).
 */
function HumanDetail({ no, arestas, nos }: { no: MapNode; arestas: MapEdge[]; nos: MapNode[] }) {
  const claims = detailValue<string[]>(no, 'claims') ?? [];
  const granted = arestas
    .filter((edge) => edge.from === no.id && edge.kind === 'human_grant')
    .map((edge) => labelOf(nos, edge.to));

  return (
    <>
      <p className={s.textoLongo} data-testid="aviso-da-lente">
        {t('accessMap.detail.lensNotice')}
      </p>

      <h4 className={s.secao}>{t('accessMap.detail.whoAsks')}</h4>
      {claims.length === 0 ? (
        <p className={s.vazioTexto}>{t('accessMap.detail.noClaim')}</p>
      ) : (
        <span className={s.chips}>
          {claims.map((texto) => (
            <Chip key={texto}>{texto}</Chip>
          ))}
        </span>
      )}

      <h4 className={s.secao}>{t('accessMap.detail.identitiesRuleReleases')}</h4>
      {granted.length === 0 ? (
        <p className={s.vazioTexto}>{t('accessMap.detail.noIdentity')}</p>
      ) : (
        <span className={s.chips}>
          {granted.map((nome) => (
            <Chip key={nome}>{nome}</Chip>
          ))}
        </span>
      )}
    </>
  );
}
